import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button, TextField, Card, Banner, BlockStack } from '@shopify/polaris';

import { useLoginMutation } from '../../warehouse/store/auth';
import { SESSION_EXPIRED_MESSAGE } from '../../warehouse/utils/errors';
import { homeRouteForPermissions } from '../../warehouse/utils/permissions';
import { useDocumentTitle } from '../../warehouse/utils/a11y';

const EMPLOYEE_LOGIN_KEY = 'employeeLogin';

const WarehouseLogin = () => {
  useDocumentTitle('Warehouse login');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [manualInput, setManualInput] = useState('');
  const [employeeLogin, setEmployeeLogin] = useState('');
  // Redirected here with ?expired=1 when an API call hit an expired session
  const [error, setError] = useState(searchParams.get('expired') ? SESSION_EXPIRED_MESSAGE : '');
  const [login, { isLoading }] = useLoginMutation();

  const handleSubmit = async () => {
    if (!employeeLogin.trim()) {
      setError('Please enter your employee login or email address');
      return;
    }

    // Validate warehouse code format: <store key>#<location id>#<UUID>
    const warehouseCodeRegex = /^[^#]+#[^#]+#[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (!warehouseCodeRegex.test(manualInput.trim())) {
      setError('Warehouse code must be in the format: STORE#LOCATION#UUID');
      return;
    }

    try {
      // On success the mutation stores the session token in localStorage
      const data = await login({
        warehouse_code: manualInput.trim(),
        employee_login: employeeLogin.trim(),
      }).unwrap();

      localStorage.setItem(EMPLOYEE_LOGIN_KEY, employeeLogin.trim());
      // Land on the first workflow the user's permissions allow
      navigate(homeRouteForPermissions(data.permissions), { replace: true });
    } catch {
      setError('Invalid employee login or warehouse code');
    }
  };

  const canSubmit = !isLoading && !!employeeLogin.trim() && !!manualInput.trim();

  return (
    <main className="warehouse-login">
      <div className="warehouse-login-header">
        <h2>Warehouse App</h2>
        <p>Log in to continue</p>
      </div>

      <div className="warehouse-login-panel">
        <Card>
          <BlockStack gap="400">
            <TextField
              label="Employee Login or Email"
              value={employeeLogin}
              onChange={(value) => {
                setEmployeeLogin(value);
                setError('');
              }}
              placeholder="Enter your login or email"
              autoComplete="off"
            />
            <TextField
              label="Warehouse Code"
              helpText="Format: STORE#LOCATION#UUID (e.g., store123#WH-A#550e8400-e29b-41d4-a716-446655440000)"
              value={manualInput}
              onChange={(value) => {
                setManualInput(value);
                setError('');
              }}
              placeholder="Enter or scan warehouse code"
              autoComplete="off"
            />

            {error ? (
              <div role="alert">
                <Banner tone="critical"><p>{error}</p></Banner>
              </div>
            ) : null}

            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Log in'}
            </Button>

            <p>
              Warehouse codes are generated on the <Link to="/login_codes">Login Codes</Link> settings page.
            </p>
          </BlockStack>
        </Card>
      </div>

      <div className="warehouse-login-panel">
        <Card>
          <p>
            Enter your employee login and warehouse code, or scan the warehouse QR code into the Warehouse Code field.
          </p>
        </Card>
      </div>
    </main>
  );
};

export default WarehouseLogin;
